import { useState, useEffect } from 'react';
import { Card, Button, Badge } from '@atoms/index';
import { adminService, PendingProduct } from '@services/adminService';
import { formatCurrency } from '@/utils/currency';
import Toast from '@/components/atoms/Toast';
import { cn } from '@/utils/cn';

export const ContentModerationTab = () => {
  const [products, setProducts] = useState<PendingProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [rejectReason, setRejectReason] = useState<{ [key: string]: string }>({});
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isVisible: false,
    message: '',
    type: 'info',
  });

  useEffect(() => {
    loadPendingProducts();
  }, [page]);

  const loadPendingProducts = async () => {
    setIsLoading(true);
    try {
      const result = await adminService.getPendingProducts(page, 20);
      setProducts(result.products);
      setTotalPages(result.pagination.totalPages);
      setTotal(result.pagination.total);
    } catch (err: any) {
      showToast('Ürünler yüklenirken bir hata oluştu', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ isVisible: true, message, type });
    setTimeout(() => {
      setToast({ ...toast, isVisible: false });
    }, 4000);
  };

  const handleApprove = async (productId: string) => {
    setProcessingId(productId);
    try {
      await adminService.approveProduct(productId);
      showToast('Ürün başarıyla onaylandı', 'success');
      await loadPendingProducts();
      setExpandedProductId(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Onaylama işlemi başarısız oldu';
      showToast(errorMessage, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (productId: string) => {
    const reason = rejectReason[productId];
    if (!reason || reason.trim().length === 0) {
      showToast('Lütfen red sebebi giriniz', 'warning');
      return;
    }

    if (reason.trim().length < 10) {
      showToast('Red sebebi en az 10 karakter olmalıdır', 'warning');
      return;
    }

    setProcessingId(productId);
    try {
      await adminService.rejectProduct(productId, reason);
      showToast('Ürün başarıyla reddedildi', 'success');
      setRejectReason({ ...rejectReason, [productId]: '' });
      await loadPendingProducts();
      setExpandedProductId(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Reddetme işlemi başarısız oldu';
      showToast(errorMessage, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const toggleExpand = (productId: string) => {
    setExpandedProductId(expandedProductId === productId ? null : productId);
  };

  if (isLoading && products.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E63946] border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Ürünler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (products.length === 0 && !isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md w-full">
          <div className="text-center py-12 px-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Tüm Ürünler Onaylandı</h3>
            <p className="text-gray-600">Onay bekleyen ürün bulunmuyor. Yeni ürünler eklendiğinde burada görünecek.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">İçerik Denetimi</h2>
          <p className="text-sm text-gray-600 mt-1">Onay bekleyen ürünleri inceleyin ve yayınlayın</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="info" className="px-4 py-2 text-sm font-semibold">
            {total} Ürün
          </Badge>
        </div>
      </div>

      <div className="grid gap-4">
        {products.map((product) => {
          const isExpanded = expandedProductId === product.id;
          const isProcessing = processingId === product.id;
          const hasRejectReason = rejectReason[product.id]?.trim().length >= 10;

          return (
            <Card
              key={product.id}
              className={cn(
                'overflow-hidden transition-all duration-300',
                isExpanded && 'ring-2 ring-[#E63946]/20 shadow-lg',
                isProcessing && 'opacity-60 pointer-events-none'
              )}
            >
              <div className="p-6">
                <div className="flex gap-6">
                  <div className="flex-shrink-0">
                    {product.imageUrl ? (
                      <div className="relative w-32 aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 shadow-md">
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-full h-full object-contain object-center"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/128?text=Ürün';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-32 aspect-[4/3] rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-md">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 truncate">{product.name}</h3>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="info" className="text-xs">
                            {product.category.name}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(product.createdAt).toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                        {product.description && (
                          <p className="text-sm text-gray-700 line-clamp-2 mb-3">{product.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className="text-2xl font-bold text-[#E63946] mb-1">
                          {formatCurrency(product.price)}
                        </div>
                        <div className="text-xs text-gray-500">Fiyat</div>
                      </div>
                    </div>

                    {!isExpanded && (
                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleApprove(product.id)}
                          disabled={isProcessing}
                          className="text-sm px-4 py-2"
                        >
                          {isProcessing ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              İşleniyor...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Onayla
                            </>
                          )}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => toggleExpand(product.id)}
                          className="text-sm px-4 py-2"
                        >
                          Detaylar
                        </Button>
                      </div>
                    )}

                    {isExpanded && (
                      <div className="pt-4 border-t border-gray-100 space-y-4 transition-all duration-300 animate-fade-in">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Red Sebebi <span className="text-red-500">*</span>
                            <span className="text-xs font-normal text-gray-500 ml-2">
                              (En az 10 karakter)
                            </span>
                          </label>
                          <textarea
                            className={cn(
                              'w-full px-4 py-3 border rounded-xl transition-all duration-200',
                              'focus:outline-none focus:ring-2 focus:ring-[#E63946]/20 focus:border-[#E63946]',
                              'placeholder:text-gray-400 text-sm',
                              hasRejectReason ? 'border-gray-300' : 'border-gray-300'
                            )}
                            rows={4}
                            value={rejectReason[product.id] || ''}
                            onChange={(e) =>
                              setRejectReason({
                                ...rejectReason,
                                [product.id]: e.target.value,
                              })
                            }
                            placeholder="Ürünün neden reddedildiğini detaylı olarak açıklayın..."
                            disabled={isProcessing}
                          />
                          <div className="mt-1 text-xs text-gray-500">
                            {rejectReason[product.id]?.length || 0} / 10 karakter
                            {hasRejectReason && (
                              <span className="text-green-600 ml-2">✓ Yeterli</span>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleApprove(product.id)}
                            disabled={isProcessing}
                            className="flex-1 text-sm px-4 py-2"
                          >
                            {isProcessing ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                İşleniyor...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Onayla ve Yayınla
                              </>
                            )}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleReject(product.id)}
                            disabled={isProcessing || !hasRejectReason}
                            className="flex-1 text-sm px-4 py-2"
                          >
                            {isProcessing ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                İşleniyor...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Reddet
                              </>
                            )}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => toggleExpand(product.id)}
                            className="text-sm px-4 py-2"
                          >
                            Kapat
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-6">
          <Button
            variant="secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
            size="sm"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Önceki
          </Button>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-gray-200 shadow-sm">
            <span className="text-sm font-medium text-gray-700">
              Sayfa <span className="font-bold text-[#E63946]">{page}</span> / {totalPages}
            </span>
          </div>
          <Button
            variant="secondary"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || isLoading}
            size="sm"
          >
            Sonraki
            <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>
      )}

      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
    </div>
  );
};
